import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import { Button } from '../ui/button';

const testimonials = [
  {
    id: 1,
    name: 'Priya Sharma',
    location: 'Mumbai, India',
    rating: 5,
    text: "An absolute gem in the Eastern Ghats! The Hotel Emerald exceeded all our expectations. The staff was incredibly warm, the rooms were luxurious, and the views were simply breathtaking. We'll definitely be returning.",
    image: 'https://static.prod-images.emergentagent.com/jobs/cb75a59d-f3d6-4479-b2b1-b017348b35ba/images/b8a2ce6a2206d6257bcb9c78feca75ea7129c6ae3c591bfa938ea338396107fa.png',
  },
  {
    id: 2,
    name: 'Rahul Menon',
    location: 'Bengaluru, India',
    rating: 5,
    text: "Perfect getaway from the city chaos. The location is stunning, surrounded by nature. The food was exceptional - especially the local Odia dishes. A truly rejuvenating experience.",
    image: 'https://static.prod-images.emergentagent.com/jobs/cb75a59d-f3d6-4479-b2b1-b017348b35ba/images/1b7a3c73245c6a7f992b16fa1964b506076704fcacd3a05d5badc641fe076715.png',
  },
  {
    id: 3,
    name: 'Anita Patel',
    location: 'Hyderabad, India',
    rating: 5,
    text: "We hosted our anniversary celebration here and it was magical. The team went above and beyond to make it special. The sunset views from our suite were unforgettable.",
    image: 'https://static.prod-images.emergentagent.com/jobs/cb75a59d-f3d6-4479-b2b1-b017348b35ba/images/7e092978d4effe765bf787331d39916efbb05f75efff5319b4b9cf98c4fb68f7.png',
  },
];

export function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const next = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="section-padding bg-background" data-testid="testimonials-section">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Guest Stories</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground">
            What Our <span className="text-emerald-700 dark:text-emerald-400">Guests</span> Say
          </h2>
        </motion.div>

        {/* Testimonials Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
              className="bg-card rounded-sm p-8 md:p-12 relative"
            >
              {/* Quote Icon */}
              <Quote className="absolute top-6 left-6 w-12 h-12 text-emerald-100 dark:text-emerald-900" />

              <div className="relative z-10">
                {/* Rating */}
                <div className="flex gap-1 mb-6 justify-center">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-gold text-gold" />
                  ))}
                </div>

                {/* Text */}
                <p className="text-lg md:text-xl text-foreground text-center leading-relaxed mb-8 font-serif italic">
                  "{testimonials[currentIndex].text}"
                </p>

                {/* Author */}
                <div className="flex items-center justify-center gap-4">
                  <img
                    src={testimonials[currentIndex].image}
                    alt={testimonials[currentIndex].name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-gold"
                  />
                  <div>
                    <p className="font-serif text-foreground font-semibold">
                      {testimonials[currentIndex].name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {testimonials[currentIndex].location}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={prev}
              className="rounded-full border-border hover:border-emerald-700 hover:text-emerald-700"
              data-testid="testimonial-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentIndex 
                      ? 'bg-emerald-700 dark:bg-emerald-400 w-6' 
                      : 'bg-border hover:bg-muted-foreground'
                  }`}
                  data-testid={`testimonial-dot-${i}`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={next}
              className="rounded-full border-border hover:border-emerald-700 hover:text-emerald-700"
              data-testid="testimonial-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
